/**
 * A ceiling on the whole-room reads a console may buy when it cannot follow the room.
 *
 * A resync is how the console recovers from a delta it cannot apply: it throws away nothing, but it asks
 * the controller for the ENTIRE room and installs that as a fresh baseline. On the production rig that
 * answer is ~200 KB, and it is not billed once — the result travels inside a Realtime Database node the Pi
 * itself subscribes to, so it crosses the metered boundary twice, and a third copy goes out over HTTPS from
 * the relay. Call it ~400 KB of egress per resync.
 *
 * The dangerous part is what drives it. A resync is issued from a Realtime Database `onValue` callback, not
 * a timer — so `noPolling.test.ts`, which reads the body of every `setInterval` / `setTimeout` in the
 * console, cannot see it, and nothing else bounded how often it could happen. Twice now a console has ended
 * up in a state where EVERY delta was unusable, and each unusable delta bought a whole room: 2 resyncs a
 * second, about 7 GB/hour, roughly $160/day, from one browser tab left open on a room running an effect. It
 * happened on 2026-09-15 (the console held no revision baseline at all) and again on 2026-09-16 (it held
 * one, read off a different clock than the delta chain — see `CONTRACT-console.md` H14). Both times the
 * console said nothing and showed nothing while it ran.
 *
 * So recovery carries its own ceiling, independent of whatever makes a delta unusable NEXT time. The point
 * of the budget is that it does not need to know: a console that cannot follow a room degrades to a slow
 * refresh instead of an unbounded one, whatever the reason turns out to be.
 *
 * **Six per minute per device.** A genuine resync is a rare event — a reconnect, a missed write, a rig
 * change, the daemon's publish gate reopening — so one every ten seconds is an order of magnitude more
 * headroom than any real cause needs, and an operator whose console does need to recover still recovers
 * within seconds. What it buys is the other end: the worst case falls from ~7 GB/hour to 6 x 60 x ~400 KB,
 * about 144 MB/hour. That is the difference between a runaway that costs $160/day and one that costs a few
 * dollars — small enough that the next divergence is a bug to fix rather than an incident to stop.
 *
 * Exhausting the budget stops the READS and nothing else. The mirror keeps delivering deltas and the
 * provider keeps reducing them, the room on screen keeps whatever it last held, and the first delta that
 * APPLIES clears the budget outright, because a console that is following the room again has no reason to
 * carry a penalty. Nothing is said to the operator: this is a bounded staleness, not something a person can
 * act on. The one thing it does say is a structured warning to the browser console, once per exhausted
 * minute — enough to name the loop in a diagnostic session, not enough to become a logging loop of its own.
 *
 * Only the reads that WERE issued are recorded. Refusals are not, so the rolling window reopens a minute
 * after the sixth read rather than a minute after the traffic stops — a console stuck behind a room that
 * never stops moving keeps retrying at the ceiling forever instead of latching shut.
 */

/** Whole-room reads one device may cost in a rolling minute when deltas cannot be applied. */
export const RESYNC_READS_PER_MINUTE = 6;

const WINDOW_MS = 60_000;

export type ResyncVerdict =
  /** Under budget: issue the whole-room read. */
  | 'allowed'
  /** This request ran the budget out. Do not read; log the warning once. */
  | 'exhausted'
  /** Already over budget this minute. Do not read, and say nothing. */
  | 'blocked';

export interface ResyncBudget {
  /** Account for one resync-driven whole-room read, and answer whether it may go out. */
  request(deviceId: string, now: number): ResyncVerdict;
  /** A delta applied: this console is following the room again, so the device starts clean. */
  clear(deviceId: string): void;
}

export function createResyncBudget(perMinute: number = RESYNC_READS_PER_MINUTE): ResyncBudget {
  /** Device → the timestamps of the reads actually issued inside the current window. */
  const issued = new Map<string, number[]>();
  /** Devices already refused this window, so the warning is raised once rather than at the mirror's rate. */
  const refused = new Set<string>();

  return {
    request(deviceId: string, now: number): ResyncVerdict {
      const recent = (issued.get(deviceId) ?? []).filter((at) => now - at < WINDOW_MS);
      if (recent.length >= perMinute) {
        issued.set(deviceId, recent);
        const first = !refused.has(deviceId);
        refused.add(deviceId);
        return first ? 'exhausted' : 'blocked';
      }
      recent.push(now);
      issued.set(deviceId, recent);
      refused.delete(deviceId);
      return 'allowed';
    },
    clear(deviceId: string): void {
      issued.delete(deviceId);
      refused.delete(deviceId);
    },
  };
}
